import React, {type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import styles from './OfferConfirmationModal.module.css';

function OfferSuccessIcon(): React.ReactElement {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="100"
			height="75"
			viewBox="0 0 100 75"
			fill="none"
			aria-hidden
			className={styles.illustration}
		>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M82.574 0.0429688H100L38.8646 74.0261L10.793 32.4281H26.9979L39.9492 51.6197L82.574 0.0429688ZM83.0518 1.05757L39.867 53.3117L26.4586 33.4427H12.7017L38.9467 72.334L97.8456 1.05757H83.0518Z"
				fill="black"
			/>
			<path
				d="M38.8567 74.0945L38.8787 73.1361L25.3243 53.0551L11.7479 32.9355L11.2853 32.3848H0L27.9184 74.0945H38.8567Z"
				fill="black"
			/>
			<path d="M71.9307 0L34.8145 44.9151L39.9091 52.4662L83.2655 0H71.9307Z" fill="black" />
		</svg>
	);
}

export type OfferConfirmationModalProps = {
	isOpen: boolean;
	title: ReactNode;
	description: ReactNode;
	confirmText: ReactNode;
	cancelText?: ReactNode;
	showIllustration?: boolean;
	onConfirm: () => void;
	onCancel: () => void;
};

export function OfferConfirmationModal({
	isOpen,
	title,
	description,
	confirmText,
	cancelText,
	showIllustration = true,
	onConfirm,
	onCancel,
}: OfferConfirmationModalProps): React.ReactElement | null {
	React.useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onCancel();
		};

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		document.addEventListener('keydown', onKeyDown);

		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen, onCancel]);

	if (!isOpen) return null;

	return createPortal(
		<div className={styles.root} role="presentation">
			<div className={styles.overlay} aria-hidden="true" onClick={onCancel} />
			<div className={styles.container}>
				<div
					className={`${styles.panel} ${showIllustration ? '' : styles.panelNoIllustration}`}
					role="dialog"
					aria-modal="true"
					data-testid="offer-modal"
				>
					{showIllustration ? <OfferSuccessIcon /> : null}

					<div className={styles.body}>
						<h2 className={styles.title}>{title}</h2>
						<div className={styles.description}>{description}</div>
					</div>

					<div className={styles.footer}>
						<button type="button" className={styles.confirmButton} onClick={onConfirm} data-testid="confirm-button">
							{confirmText}
						</button>
						{cancelText ? (
							<button type="button" className={styles.cancelButton} onClick={onCancel} data-testid="cancel-button">
								{cancelText}
							</button>
						) : null}
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
}
